import React from 'react';
import { ArrowLeft } from 'lucide-react';
import OrderStatusPill from '../shared/OrderStatusPill';
import B2BTag from '../shared/B2BTag';
import PaymentStatusBadge from '../../../Common/PaymentStatusBadge';
import DownloadInvoiceButton from '../DownloadInvoiceButton';
import OrderActionsMenu from './OrderActionsMenu';
import { Button, CopyButton } from './primitives';
import { formatDateTime, formatRelative } from '../shared/orderFormat';

/*
 * Identity, live state, and the actions that apply to the whole order.
 *
 * The metadata strip below the title carries the six facts that used to occupy
 * a seven-field "General" column further down the page — channel, payment
 * method, item count and so on. Nothing here is repeated anywhere else on the
 * screen; the old design printed the placed date three times and the payment
 * status twice.
 *
 * No refund state or action lives here: refunds are made by hand in the
 * payment provider's dashboard and never recorded back, so a "Refunded X of Y"
 * figure would only ever show part of the story.
 */
const OrderCommandBar = ({
  order,
  statusLabel,
  itemCount,
  hasB2B,
  onBack,
  orderId,
  nextStep,
  onAdvance,
  isAdvancing,
  isEditable,
  statusOptions,
}) => {
  const placedAt = formatDateTime(order.created_on);
  const placedAgo = formatRelative(order.created_on);
  const orderNumber = order.order_number || order.order_id;

  return (
    <header className="mb-4">
      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-1.5 mb-3 bg-transparent border-0 p-0 text-[12.5px] text-nysmah-muted hover:text-nysmah-dark cursor-pointer transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-nysmah-accent rounded"
      >
        <ArrowLeft className="w-3.5 h-3.5" aria-hidden="true" />
        Back to orders
      </button>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2.5 flex-wrap">
            <h1 className="text-xl font-semibold text-nysmah-dark m-0 leading-none tracking-tight tabular-nums">
              #{orderNumber}
            </h1>
            <CopyButton value={orderNumber} label="Copy order number" />
            <OrderStatusPill statusId={order.status} label={statusLabel} />
            <PaymentStatusBadge value={order.payment_status} />
            {hasB2B && <B2BTag size="md" showIcon />}
          </div>

          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-2 text-[12px] text-nysmah-muted">
            <span>
              Placed{' '}
              <b className="font-medium text-[#4A5B66]">{placedAt}</b>
              {placedAgo && ` · ${placedAgo}`}
            </span>
            {order.order_from_label && (
              <>
                <span aria-hidden="true" className="text-[#C4CFD5]">·</span>
                <span>
                  via <b className="font-medium text-[#4A5B66]">{order.order_from_label}</b>
                </span>
              </>
            )}
            {(order.payment_method_label || order.paymentMethod) && (
              <>
                <span aria-hidden="true" className="text-[#C4CFD5]">·</span>
                <span>
                  paid by{' '}
                  <b className="font-medium text-[#4A5B66]">
                    {order.payment_method_label || order.paymentMethod}
                  </b>
                </span>
              </>
            )}
            <span aria-hidden="true" className="text-[#C4CFD5]">·</span>
            <span>
              <b className="font-medium text-[#4A5B66]">{itemCount}</b>{' '}
              {itemCount === 1 ? 'item' : 'items'}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <DownloadInvoiceButton
            orderId={orderId}
            label="Invoice"
            loadingLabel="Generating…"
            className="inline-flex items-center justify-center gap-1.5 font-medium rounded-lg border bg-white border-[#E1E7EA] text-[#4A5B66] hover:bg-[#F5F8F8] hover:text-nysmah-dark transition-colors whitespace-nowrap text-[12.5px] px-3 py-1.5 disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-nysmah-accent"
          />
          {nextStep && (
            <Button
              variant="primary"
              onClick={() => onAdvance(nextStep.id)}
              disabled={isAdvancing}
            >
              {isAdvancing ? 'Saving…' : `Mark ${nextStep.label}`}
            </Button>
          )}
          {isEditable && statusOptions.length > 0 && (
            <OrderActionsMenu
              statusOptions={statusOptions}
              currentStatusId={order.status}
              onSelectStatus={onAdvance}
            />
          )}
        </div>
      </div>
    </header>
  );
};

export default OrderCommandBar;
